import React, { useState, useEffect } from 'react';
import { parseMarkdown, extractTitle, extractDate, formatDate, extractExcerpt } from './markdownParser';
import { useWindowWidth } from './useWindowWidth';

const BRAND = {
  navy: '#1a2a5e',
  navyDark: '#111d42',
  navyDeep: '#0b1228',
  red: '#c41230',
  redMuted: 'rgba(196,18,48,0.15)',
  white: '#ffffff',
  offWhite: '#e8eaf0',
  offWhiteDim: 'rgba(232,234,240,0.65)',
  offWhiteFaint: 'rgba(232,234,240,0.35)',
  border: 'rgba(255,255,255,0.1)',
  borderStrong: 'rgba(255,255,255,0.18)',
  panelBg: 'rgba(26,42,94,0.35)',
  panelBorder: 'rgba(255,255,255,0.09)',
};

function PostBody({ html }) {
  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
      style={{
        fontFamily: "'Crimson Pro', serif",
        fontSize: 17,
        lineHeight: 1.85,
        color: BRAND.offWhite,
        maxWidth: '100%',
      }}
    />
  );
}

export default function MiniBlogPage({ sidebarTop = 185 }) {
  const [posts, setPosts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < 768;

  useEffect(() => {
    async function loadPublishedPosts() {
      try {
        const manifestRes = await fetch('/mini-posts/mini-manifest.json');
        if (!manifestRes.ok) throw new Error('No manifest');
        const filenames = await manifestRes.json();
        const fetched = await Promise.all(
          filenames.map(async (filename) => {
            const res = await fetch(`/mini-posts/${filename}`);
            if (!res.ok) return null;
            const raw = await res.text();
            const title = extractTitle(raw, filename);
            const date = extractDate(raw, filename);
            return { id: filename, title, date, filename, raw, published: true };
          })
        );
        const valid = fetched.filter(Boolean).sort((a, b) => b.date - a.date);
        setPosts(valid);
        if (valid.length > 0) setActiveId(valid[0].id);
      } catch (e) {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    }
    loadPublishedPosts();
  }, []);

  const activePost = posts.find(p => p.id === activeId) || posts[0] || null;
  const renderedHtml = activePost ? parseMarkdown(activePost.raw) : '';

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '80px 24px',
        fontFamily: "'Crimson Pro', serif", fontStyle: 'italic',
        color: BRAND.offWhiteFaint, fontSize: 15,
      }}>
        Loading posts...
      </div>
    );
  }

  const archiveList = (
    <div>
      <div style={{
        fontFamily: "'Cinzel', serif", fontSize: 8, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: BRAND.offWhiteFaint, marginBottom: 12,
      }}>
        Previous Posts
      </div>
      {posts.length === 0 ? (
        <p style={{ fontFamily: "'Crimson Pro', serif", fontStyle: 'italic', fontSize: 13, color: BRAND.offWhiteFaint, lineHeight: 1.6 }}>
          No posts yet.
        </p>
      ) : (
        <div>
          {posts.map((post) => {
            const isActive = post.id === activeId;
            const excerpt = extractExcerpt(post.raw, 110);
            return (
              <div key={post.id}
                onClick={() => { setActiveId(post.id); if (isMobile) setShowArchive(false); }}
                style={{ padding: '14px 0', borderBottom: `1px solid ${BRAND.panelBorder}`, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <div style={{
                  fontSize: 10, fontFamily: "'Crimson Pro', serif", fontStyle: 'italic',
                  color: isActive ? BRAND.red : BRAND.offWhiteFaint, marginBottom: 4,
                }}>
                  {formatDate(post.date)}
                </div>
                <div style={{
                  fontFamily: "'Cinzel', serif", fontSize: 11, lineHeight: 1.35,
                  color: isActive ? BRAND.white : BRAND.offWhiteDim,
                  borderLeft: isActive ? `2px solid ${BRAND.red}` : '2px solid transparent',
                  paddingLeft: 8, marginBottom: excerpt ? 6 : 0,
                }}>
                  {post.title}
                </div>
                {excerpt && (
                  <div style={{
                    fontFamily: "'Crimson Pro', serif", fontSize: 12,
                    color: BRAND.offWhiteFaint, lineHeight: 1.5,
                    paddingLeft: 10,
                  }}>
                    {excerpt}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ paddingBottom: 60 }}>
        {posts.length > 1 && (
          <button
            onClick={() => setShowArchive(o => !o)}
            style={{
              width: '100%', padding: '10px 16px', marginBottom: 20,
              background: BRAND.panelBg, border: `1px solid ${BRAND.borderStrong}`,
              borderRadius: 6, color: BRAND.offWhiteDim,
              fontFamily: "'Cinzel', serif", fontSize: 9, letterSpacing: '0.15em',
              textTransform: 'uppercase', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
            <span>Previous Posts</span>
            <span style={{ fontSize: 14, opacity: 0.6 }}>{showArchive ? '▲' : '▼'}</span>
          </button>
        )}
        {showArchive && (
          <div style={{
            background: BRAND.panelBg, border: `1px solid ${BRAND.panelBorder}`,
            borderRadius: 6, padding: '16px', marginBottom: 24,
          }}>
            {archiveList}
          </div>
        )}
        {activePost && (
          <>
            <div style={{
              display: 'inline-block', fontFamily: "'Cinzel', serif", fontSize: 9,
              letterSpacing: '0.2em', textTransform: 'uppercase', color: BRAND.red,
              border: `1px solid ${BRAND.red}`, padding: '3px 10px', borderRadius: 2, marginBottom: 16, opacity: 0.9,
            }}>
              Minis
            </div>
            <h1 style={{
              fontFamily: "'Cinzel', serif", fontSize: 'clamp(20px, 6vw, 32px)',
              fontWeight: 600, lineHeight: 1.15, color: BRAND.white, marginBottom: 12,
            }}>
              {activePost.title}
            </h1>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28,
              color: BRAND.offWhiteFaint, fontSize: 12,
              fontFamily: "'Crimson Pro', serif", fontStyle: 'italic',
            }}>
              <span style={{ width: 24, height: 1, background: BRAND.red, display: 'inline-block' }} />
              {formatDate(activePost.date)}
            </div>
            <PostBody html={renderedHtml} />
          </>
        )}
        {!activePost && (
          <div style={{ fontFamily: "'Crimson Pro', serif", fontStyle: 'italic', fontSize: 15, color: BRAND.offWhiteFaint, marginTop: 20, lineHeight: 1.7 }}>
            No posts found. Add files to <code style={{ fontStyle: 'normal', background: 'rgba(255,255,255,0.07)', padding: '1px 6px', borderRadius: 3 }}>public/mini-posts/</code> and update <code style={{ fontStyle: 'normal', background: 'rgba(255,255,255,0.07)', padding: '1px 6px', borderRadius: 3 }}>mini-manifest.json</code>.
          </div>
        )}
      </div>
    );
  }

  // Desktop layout
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      minHeight: 'calc(100vh - 188px)',
      paddingTop: 32,
      boxSizing: 'border-box',
    }}>

      {/* LEFT: logo column */}
      <div style={{
        width: '10%', flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 16, paddingLeft: 16, paddingRight: 16,
        borderRight: `1px solid ${BRAND.panelBorder}`,
        position: 'sticky', top: 0, alignSelf: 'flex-start',
        height: 'calc(100vh - 188px)',
      }}>
        <img src="/LogoTall.png" alt="CyberMod.io" style={{ width: '100%', mixBlendMode: 'luminosity', opacity: 0.9 }} />
      </div>

      {/* CENTRE: post content */}
      <main style={{ width: '65%', flexShrink: 0, paddingLeft: 40, paddingRight: 40, paddingBottom: 80 }}>
        {activePost ? (
          <>
            <div style={{
              display: 'inline-block', fontFamily: "'Cinzel', serif", fontSize: 9,
              letterSpacing: '0.2em', textTransform: 'uppercase', color: BRAND.red,
              border: `1px solid ${BRAND.red}`, padding: '3px 10px', borderRadius: 2, marginBottom: 20, opacity: 0.9,
            }}>
              Minis
            </div>
            <h1 style={{
              fontFamily: "'Cinzel', serif", fontSize: 'clamp(22px, 3.5vw, 38px)',
              fontWeight: 600, lineHeight: 1.15, color: BRAND.white, marginBottom: 16, letterSpacing: '0.04em',
            }}>
              {activePost.title}
            </h1>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 16, marginBottom: 40,
              color: BRAND.offWhiteFaint, fontSize: 12,
              fontFamily: "'Crimson Pro', serif", fontStyle: 'italic', letterSpacing: '0.05em',
            }}>
              <span style={{ width: 28, height: 1, background: BRAND.red, display: 'inline-block', opacity: 0.7 }} />
              {formatDate(activePost.date)}
            </div>
            <PostBody html={renderedHtml} />
          </>
        ) : (
          <div style={{ fontFamily: "'Crimson Pro', serif", fontStyle: 'italic', fontSize: 15, color: BRAND.offWhiteFaint, marginTop: 20, lineHeight: 1.7 }}>
            No posts found. Add files to <code style={{ fontStyle: 'normal', background: 'rgba(255,255,255,0.07)', padding: '1px 6px', borderRadius: 3 }}>public/mini-posts/</code> and update <code style={{ fontStyle: 'normal', background: 'rgba(255,255,255,0.07)', padding: '1px 6px', borderRadius: 3 }}>mini-manifest.json</code>.
          </div>
        )}
      </main>

      {/* RIGHT: archive */}
      <aside style={{
        width: '25%', flexShrink: 0,
        position: 'sticky', top: 0, alignSelf: 'flex-start',
        height: 'calc(100vh - 188px)', overflowY: 'auto',
        borderLeft: `1px solid ${BRAND.panelBorder}`,
        paddingLeft: 24, paddingRight: 16, paddingTop: 16,
      }}>
        {archiveList}
      </aside>

    </div>
  );
}
